import { coreApi } from '@cp/shared/api/core';
import { generateApiUrl } from '@common/api/configure';
import { ApiRoute } from '@common/api/route';
import { apiRouteMethodDict } from '@common/api/routeMethod';
import { ApiRouteBody } from '@common/api/routeBody';
import { DealCalcPriceResponse, DealConsumerSchedule, DealFeedbackExistedResponse, DealViewModel } from '@common/model/deal';
import { ApiRoutePathParams } from '@common/api/routePathParams';
import type { DealListRequest, DealListResponse } from '@common/model/deal';
import { coreCurrentClientApi } from '@cp/entities/client';

export const coreDealApi = coreApi.injectEndpoints({
  endpoints: (builder) => ({
    createWithClient: builder.mutation<DealViewModel, ApiRouteBody[ApiRoute.DealCreateWithClient]>({
      query: (body) => ({
        url: generateApiUrl(ApiRoute.DealCreateWithClient),
        method: apiRouteMethodDict[ApiRoute.DealCreateWithClient],
        body,
      }),
      async onQueryStarted({ address }, { dispatch, queryFulfilled }) {
        if (!address) {
          return;
        }

        // Address is synced to profile asynchronously on the server; patch cache to avoid stale refetch.
        const patchResult = dispatch(
          coreCurrentClientApi.util.updateQueryData('getProfile', undefined, (draft) => {
            draft.address = address;
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    calcPrice: builder.mutation<DealCalcPriceResponse, ApiRouteBody[ApiRoute.DealCalcPrice]>({
      query: (body) => ({
        url: generateApiUrl(ApiRoute.DealCalcPrice),
        method: apiRouteMethodDict[ApiRoute.DealCalcPrice],
        body,
      }),
    }),

    consumerSchedule: builder.mutation<DealConsumerSchedule, ApiRouteBody[ApiRoute.DealConsumerSchedule]>({
      query: (body) => ({
        url: generateApiUrl(ApiRoute.DealConsumerSchedule),
        method: apiRouteMethodDict[ApiRoute.DealConsumerSchedule],
        body,
      }),
    }),

    accept: builder.mutation<void, ApiRoutePathParams[ApiRoute.DealAccept]>({
      query: (params) => ({
        url: generateApiUrl(ApiRoute.DealAccept, params),
        method: apiRouteMethodDict[ApiRoute.DealAccept],
      }),
    }),

    reject: builder.mutation<void, ApiRoutePathParams[ApiRoute.DealReject] & ApiRouteBody[ApiRoute.DealReject]>({
      query: ({ dealId, ...body }) => ({
        url: generateApiUrl(ApiRoute.DealReject, { dealId }),
        method: apiRouteMethodDict[ApiRoute.DealReject],
        body,
      }),
    }),

    updatePrice: builder.mutation<DealViewModel, ApiRoutePathParams[ApiRoute.DealUpdatePrice] & ApiRouteBody[ApiRoute.DealUpdatePrice]>({
      query: ({ dealId, ...body }) => ({
        url: generateApiUrl(ApiRoute.DealUpdatePrice, { dealId }),
        method: apiRouteMethodDict[ApiRoute.DealUpdatePrice],
        body,
      }),
    }),

    createFeedback: builder.mutation<void, ApiRoutePathParams[ApiRoute.DealFeedbacksCreate] & ApiRouteBody[ApiRoute.DealFeedbacksCreate]>({
      query: ({ dealId, ...body }) => ({
        url: generateApiUrl(ApiRoute.DealFeedbacksCreate, { dealId }),
        method: apiRouteMethodDict[ApiRoute.DealFeedbacksCreate],
        body,
      }),
    }),

    getOne: builder.query<DealViewModel, ApiRoutePathParams[ApiRoute.DealGetOne]>({
      query: (params) => ({
        url: generateApiUrl(ApiRoute.DealGetOne, params),
        method: apiRouteMethodDict[ApiRoute.DealGetOne],
      }),
    }),

    isFeedbackExisted: builder.query<DealFeedbackExistedResponse, { dealId: number; authorId: number }>({
      query: ({ dealId, ...params }) => ({
        url: generateApiUrl(ApiRoute.DealFeedbackExisted, { dealId }),
        method: apiRouteMethodDict[ApiRoute.DealFeedbackExisted],
        params,
      }),
    }),

    userDeals: builder.query<DealListResponse, DealListRequest>({
      query: (params) => ({
        url: '/deals',
        params,
      }),
    }),
  }),
});
